import { Permission, type GetMeasuringPointsQuery, type SwarmType } from '@/graphql/types';
import { hasAtLeastPermission, requirePermission } from '../sharing/utils';
import { resolveSwarmTypeFromGraphqlTypename } from '../../../utils/swarm-type';
import { graphqlTimezoneToIana } from '../../../utils/timezone';
import { isNonNullable } from '../../../utils/typeGuards';

type QueryMeasuringPoint = NonNullable<
    NonNullable<GetMeasuringPointsQuery['measuringPoints']>[number]
>;

export type QueryProject = NonNullable<NonNullable<GetMeasuringPointsQuery['projects']>[number]>;

export interface PreviewProjectModel {
    id: string;
    name: string;
}

export interface PreviewMeasuringPointModel {
    id: string;
    name: string;
    deviceName: string | null;
    projectId: string | null;
    swarmType: SwarmType;
    permission: Permission;
    timezone: string;
}

export function requireProjectTimezone(entry: QueryProject): string {
    if (typeof entry.timezone !== 'string' || entry.timezone.trim().length === 0) {
        throw new Error(`Missing timezone for project "${entry.id}".`);
    }

    return graphqlTimezoneToIana(entry.timezone);
}

function requireMeasuringPointTimezone(entry: QueryMeasuringPoint): string {
    if (entry.timezone === undefined) {
        throw new Error(`Missing timezone field for measuring point "${entry.id}".`);
    }
    // Same fallback as the export: `get_sensor_timezone()` defaults to UTC.
    return entry.timezone === null ? 'UTC' : graphqlTimezoneToIana(entry.timezone);
}

function requireDeviceName(entry: QueryMeasuringPoint): string | null {
    if (entry.deviceName === null) {
        return null;
    }

    if (typeof entry.deviceName === 'undefined') {
        throw new Error(`Missing device name field for measuring point "${entry.id}".`);
    }

    return entry.deviceName;
}

function requireProjectId(entry: QueryMeasuringPoint): string | null {
    if (entry.project === null) {
        return null;
    }

    if (typeof entry.project === 'undefined') {
        throw new Error(`Missing project field for measuring point "${entry.id}".`);
    }

    return entry.project;
}

// Reference-only projects can be named but not reported on, so they are never offered as a preview.
export function getPreviewableProjects(data: GetMeasuringPointsQuery): QueryProject[] {
    const { projects } = data;
    if (!projects) {
        throw new Error('Missing projects payload for preview context.');
    }

    return projects
        .filter(isNonNullable)
        .filter((project) =>
            hasAtLeastPermission(
                requirePermission(project.permission, `project "${project.id}"`),
                Permission.View
            )
        );
}

export function getPreviewProjects(data: GetMeasuringPointsQuery): PreviewProjectModel[] {
    return getPreviewableProjects(data).map((project) => ({
        id: project.id,
        name: project.name,
    }));
}

export function getPreviewMeasuringPoints(
    data: GetMeasuringPointsQuery
): PreviewMeasuringPointModel[] {
    const { measuringPoints } = data;
    if (!measuringPoints) {
        throw new Error('Missing measuring points payload for preview context.');
    }

    return measuringPoints.filter(isNonNullable).map((entry) => ({
        id: entry.id,
        name: entry.name,
        deviceName: requireDeviceName(entry),
        projectId: requireProjectId(entry),
        swarmType: resolveSwarmTypeFromGraphqlTypename(entry.__typename),
        permission: requirePermission(entry.permission, `measuring point "${entry.id}"`),
        timezone: requireMeasuringPointTimezone(entry),
    }));
}
